import React from "react";
import PropTypes from "prop-types";
import ReactPaginate from "react-paginate";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  getFilteredRowModel,
} from "@tanstack/react-table";
import noImg from "../assets/nodata.png";

const Table = ({
  columns,
  data,
  noOfPages,       // total pages available
  currentPage,     // controlled page number (1-based)
  setCurrentPage,  // setter for page number
  showFilters = false,
}) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const headerGroups = table.getHeaderGroups();

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected + 1);  // convert 0-based to 1-based
  };

  return (
    <>
      <div className="overflow-auto">
        <table className="min-w-full border-collapse rounded-lg whitespace-nowrap">
          <thead className="bg-[#EFEFF0] h-14 rounded-lg">
            {headerGroups.map(group => (
              <tr key={group.id} className="rounded-lg border-b-2 border-gray-500">
                {group.headers.map(header => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="px-4 py-2 text-left text-[14px] whitespace-nowrap font-semibold text-gray-900"
                  >
                    <div
                      className={header.column.getCanSort() ? "cursor-pointer select-none" : ""}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <span className="ml-1 material-symbols-outlined text-xs">expand_less</span>,
                        desc: <span className="ml-1 material-symbols-outlined text-xs">expand_more</span>,
                      }[header.column.getIsSorted()] ?? null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}

            {showFilters && headerGroups.map(group => (
              <tr key={group.id + "-filter"} className="border-b border-gray-300">
                {group.headers.map(header => (
                  <th key={header.id + "-filter"} className="px-4 py-2">
                    {header.column.getCanFilter()
                      ? <Filter column={header.column} table={table} />
                      : null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4 text-gray-500">
                  <div className="flex flex-col items-center">
                    <img src={noImg} alt="No data" className="h-48 mb-4" />
                    <p className="text-lg">No data found.</p>
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <tr key={row.id} className="border-b border-gray-300 hover:bg-gray-100">
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-4 py-2 text-sm text-gray-900">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center items-center mt-4">
        <ReactPaginate
          previousLabel={<span className="material-symbols-outlined">chevron_left</span>}
          nextLabel={<span className="material-symbols-outlined">chevron_right</span>}
          breakLabel={"..."}
          pageCount={Math.ceil(noOfPages/10)}
          forcePage={currentPage - 1}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={handlePageClick}
          containerClassName="pagination"
          activeClassName="active"
        />
      </div>
    </>
  );
};

Table.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  noOfPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  showFilters: PropTypes.bool,
};

export default Table;

// --- Filter Component ---
function Filter({ column, table }) {
  const firstValue = table.getPreFilteredRowModel().flatRows[0]?.getValue(column.id);
  const columnFilterValue = column.getFilterValue();

  return typeof firstValue === "number" ? (
    <div className="flex space-x-2">
      <input
        type="number"
        value={columnFilterValue?.[0] ?? ""}
        onChange={e => column.setFilterValue(old => [e.target.value, old?.[1]])}
        placeholder="Min"
        className="w-20 p-2 border border-gray-300 rounded"
      />
      <input
        type="number"
        value={columnFilterValue?.[1] ?? ""}
        onChange={e => column.setFilterValue(old => [old?.[0], e.target.value])}
        placeholder="Max"
        className="w-20 p-2 border border-gray-300 rounded"
      />
    </div>
  ) : (
    <input
      type="text"
      value={columnFilterValue ?? ""}
      onChange={e => column.setFilterValue(e.target.value)}
      placeholder="Search..."
      className="w-full p-2 border border-gray-300 rounded"
    />
  );
}

Filter.propTypes = {
  column: PropTypes.object.isRequired,
  table: PropTypes.object.isRequired,
};
